import Route from '@ember/routing/route';
import { getOwner } from '@ember/owner';
import type MetaService from 'madow-tech/services/meta';

export default class ContactSuccessRoute extends Route {
  model() {
    // Get intl service
    const owner = getOwner(this);
    if (!owner) return {};

    const intl = owner.lookup('service:intl');
    if (!intl) return {};

    // Get locale preference from localStorage (preferred) or cookie (fallback)
    const preferredLocale = this.getPreferredLocale();

    // Set locale based on user preference
    intl.setLocale([preferredLocale]);

    // Persist locale preference in localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem('locale', preferredLocale);
      } catch (e) {
        // localStorage might be disabled, fall back to cookie
        document.cookie = `locale=${preferredLocale};path=/;max-age=31536000`;
      }
    } else if (typeof document !== 'undefined') {
      // Fallback to cookie if localStorage not available
      document.cookie = `locale=${preferredLocale};path=/;max-age=31536000`;
    }

    // Set up SEO metadata
    this.setupSEO(owner, preferredLocale);

    // Fire Google Analytics conversion event
    this.trackConversion();

    return {};
  }

  private getPreferredLocale(): 'en' | 'es' {
    if (typeof window === 'undefined') return 'en';

    // Try localStorage first
    if (window.localStorage) {
      try {
        const stored = window.localStorage.getItem('locale');
        if (stored === 'en' || stored === 'es') {
          return stored;
        }
      } catch (e) {
        // localStorage might be disabled
      }
    }

    // Fallback to cookie
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'locale' && (value === 'en' || value === 'es')) {
          return value;
        }
      }
    }

    // Default to English
    return 'en';
  }

  private setupSEO(owner: ReturnType<typeof getOwner>, locale: string) {
    const meta = owner?.lookup('service:meta') as MetaService | undefined;
    if (!meta) return;

    // Reset previous meta
    meta.reset();

    // Set basic metadata (localized based on user's preference)
    if (locale === 'es') {
      meta.title = '¡Gracias por contactarnos!';
      meta.description =
        'Hemos recibido tu mensaje y te responderemos en un día.';
    } else {
      meta.title = "Thank you for contacting us!";
      meta.description =
        "We've received your message and will get back to you within a day.";
    }
    meta.canonicalUrl = 'https://madow.tech/contact-success';

    // Set hreflang alternate languages
    meta.alternateLanguages = [
      { lang: 'en', url: 'https://madow.tech/contact-success' },
      { lang: 'es', url: 'https://madow.tech/contact-success' },
    ];

    // Set custom meta tags - noindex, nofollow for conversion page
    meta.setTags([
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
      {
        property: 'og:locale',
        content: locale === 'es' ? 'es_AR' : 'en_US',
      },
      {
        property: 'og:locale:alternate',
        content: locale === 'es' ? 'en_US' : 'es_AR',
      },
      { name: 'language', content: locale === 'es' ? 'Spanish' : 'English' },
    ]);

    // Render meta to header
    meta.renderMetaToHeader();
  }

  private trackConversion() {
    if (typeof window === 'undefined') return;

    // Track conversion event for Google Ads
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'lead_submission', {
        event_category: 'contact_form',
        event_label: 'contact_success',
        value: 1,
      });
    }

    // Also push to dataLayer for enhanced conversions
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'lead_submission',
        event_category: 'contact_form',
        event_label: 'contact_success',
        value: 1,
      });
    }
  }
}



